<template>
  <div
    class="map-wrapper"
  >
    <div
      ref="mapDiv"
      :id="uniqueId"
      class="google-map-transition google-map"
      :style="getMapStyle()"
      :class="getMapClasses()"
    />
<!--    Loader that will be shown when map tile is not loaded yet-->
    <v-overlay
      v-if="!showMap"
      style="left: 12px; bottom: 12px; border-radius: 16px;"
      opacity="0.2"
      absolute
    >
      <v-progress-circular
        indeterminate
        :color="'var(--v-primary-base)'"
        size="60"
      />
    </v-overlay>
<!--    Map type chooser positioned at the bottom-left side of the map-->
    <div
      v-show="mapInitialized && showMapTypeChooserControl"
      ref="mapTypeChooser"
      class="map-type-chooser"
    >
      <MapTypeChooser
        @update:map-type="type => updateMapType(type)"
        @opened="onMapTypeChooseOpen"
      />
    </div>
<!--    Search button-->
    <div
      v-show="mapInitialized && showSearchControl"
      class="search-container"
    >
      <div
        ref="searchInput"
      >
        <v-autocomplete
          ref="search-autocomplete"
          v-model="chosenSearchValue"
          id="search"
          :items="searchValueItems"
          item-text="label"
          :item-value="value => value.x + ',' + value.y"
          class="search-input"
          :loading="showSearchLoading"
          :clearable="searchExpanded"
          :hide-details="false"
          :search-input.sync="searchValue"
          :placeholder="searchExpanded ? $t('base.search_address') : ''"
          :append-icon="searchExpanded ? '' : 'mdi-magnify'"
          return-object
          @change="onChooseAddress"
          @focusin="expandSearch"
          @blur="collapseSearch"
          @click:clear="onSearchClearClick"
        />
      </div>
    </div>

<!--    Custom map control, added dynamically-->
    <div
      v-show="mapInitialized"
    >
      <slot
        name="custom-control"
      />
    </div>
  </div>
</template>

<script>
import { googleMap } from '@/global/components/google-map/googlemap'
import MapTypeChooser from '@/global/components/google-map/components/MapTypeChooser.vue'
import { createNamespacedHelpers } from 'vuex'
import { debounce, isObject } from 'lodash'
import { api } from '@/global/services/api'

const { mapActions: mapActionsTracking, mapGetters } = createNamespacedHelpers('satellite-tracking/live-tracking')
const DEFAULT_MAP_ZOOM = 8
const SEARCH_ADDRESS_DELAY_IN_MS = 500
const DEFAULT_MAP_SEARCH_ZOOM_LEVEL = 16
export default {
  name: 'GoogleMap',

  components: {
    MapTypeChooser
  },

  props: {
    config: {
      type: Object,
      default: () => ({})
    },
    uniqueId: {
      type: String,
      default: 'google-map'
    },
    externalMapTypeSet: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      googleMaps: null,
      map: null,
      mapInitialized: false,
      currentTileLayer: null,
      trafficLayer: null,
      searchValue: null,
      searchExpanded: false,
      searchValueItems: [],
      chosenSearchValue: null,
      showSearchLoading: false,
      oldChosenSearchValue: null,
      searchMarker: null,
      AdvancedMarker: null,
      showMap: false
    }
  },

  computed: {
    ...mapGetters(['viewConfig']),

    mapType () {
      return this.$store.getters['map-type/getMapType']
    },

    showMapTypeChooserControl () {
      return this.config?.controls?.mapTypeChooserControl ?? false
    },

    showZoomControl () {
      return this.config?.controls?.zoomControl ?? false
    },

    zoomControlOptions () {
      return this.config?.controls?.zoomControlOptions ?? {
        position: this.googleMaps.ControlPosition.TOP_LEFT
      }
    },

    showCameraControl () {
      return this.config?.controls?.cameraControl ?? false
    },

    showMapTypeControl () {
      return this.config?.controls?.mapTypeControl ?? false
    },

    showScaleControl () {
      return this.config?.controls?.scaleControl ?? false
    },

    showStreetViewControl () {
      return this.config?.controls?.streetViewControl ?? false
    },

    showRotateControl () {
      return this.config?.controls?.rotateControl ?? false
    },

    showFullscreenControl () {
      return this.config?.controls?.fullscreenControl ?? false
    },

    mapZoom () {
      return this.config?.zoom ?? DEFAULT_MAP_ZOOM
    },

    showSearchControl () {
      return this.config?.controls?.searchControl ?? false
    },

    disablePoiClicks () {
      return this.config?.disablePoiClicks ?? false
    }
  },

  watch: {
    searchValue: debounce(async function (val) {
      await this.fetchSearchResults(val)
    }, SEARCH_ADDRESS_DELAY_IN_MS)
  },

  beforeDestroy () {
    this.map = null
    this.googleMaps = null
  },

  async mounted () {
    try {
      const googleMaps = await googleMap.loadGoogleMaps()
      this.googleMaps = googleMaps
      const mapDiv = document.getElementById(this.uniqueId)

      // Map initialization
      if (mapDiv && googleMaps && googleMaps.Map) {
        this.map = new googleMaps.Map(mapDiv, {
          center: { lat: 45.8150, lng: 15.9819 },
          zoom: this.mapZoom,
          maxZoom: 20,
          mapId: googleMap.mapId,
          zoomControl: this.showZoomControl,
          zoomControlOptions: this.zoomControlOptions,
          cameraControl: this.showCameraControl,
          mapTypeControl: this.showMapTypeControl,
          scaleControl: this.showScaleControl,
          streetViewControl: this.showStreetViewControl,
          rotateControl: this.showRotateControl,
          fullscreenControl: this.showFullscreenControl,
          gestureHandling: 'greedy', // Disable mobile zoom controls on desktop,
          clickableIcons: !this.disablePoiClicks // Disable POI clicks
        })
      }

      if (this.map && this.googleMaps) {
        // Show a map when a tile is loaded
        this.map.addListener('tilesloaded', async () => {
          if (!this.mapInitialized) {
            this.$refs?.mapDiv?.classList?.add('loaded')
            this.showMap = true

            this.mapInitialized = true
            const { AdvancedMarkerElement } = await this.googleMaps.importLibrary('marker')
            this.AdvancedMarker = AdvancedMarkerElement

            // Emit map objects
            this.$emit('map:objects', {
              map: this.map,
              googleMaps: this.googleMaps,
              advancedMarker: this.AdvancedMarker
            })

            // Map type chooser control
            if (this.showMapTypeChooserControl) {
              this.map.controls[googleMaps.ControlPosition.LEFT_BOTTOM].push(this.$refs.mapTypeChooser)
            }

            // Map search input positioned in the maps left-top corner
            if (this.showSearchControl) {
              const input = this.$refs.searchInput
              this.map.controls[this.googleMaps.ControlPosition.LEFT_TOP].push(input)
            }

            // Emit map click event
            this.map.addListener('click', (event) => {
              // Prevent POI click and showing the popup info window if user set disablePoiClicks to true
              if (this.disablePoiClicks) {
                if (event.placeId) {
                  event.stop()
                }
              }

              this.$emit('map:click', {
                event,
                map: this.map,
                googleMaps: this.googleMaps,
                advancedMarker: this.AdvancedMarker
              })
            })
          }
        })
      }
    }
    catch (exception) {
      console.error(exception)
    }
  },

  async created () {
    // Fetch map tiles from store
    await this.fetch()
    // Update map type in map chooser
    if (this.externalMapTypeSet) {
      const foundMapType = this.viewConfig?.mapTypeToggler?.find(mapType => mapType?.key === this.externalMapTypeSet)
      this.updateMapType(foundMapType)
    }
    else {
      this.updateMapType(this.mapType)
    }
  },

  methods: {
    ...mapActionsTracking(['fetch', 'setMapType']),

    // Fetch search results and populate autocomplete values
    async fetchSearchResults (val) {
      try {
        this.showSearchLoading = true
        if (val === null) {
          this.showSearchLoading = false
          return
        }

        // Call an api to search address
        const response = await api('satellite-tracking').get('/map-search', {
          mapType: 'here',
          q: val
        })
        // If the response is an array, map those values to the autocomplete search values
        if (response && response.length) {
          this.searchValueItems = [...response]
        }
        // Otherwise, set autocomplete values to an empty array []
        else {
          this.searchValueItems = []
          this.oldChosenSearchValue = null
          if (this.searchMarker) {
            this.searchMarker.setMap(null)
          }
        }
        this.showSearchLoading = false
      }
      catch (exception) {
        this.showSearchLoading = false
        this.searchValueItems = []
        this.oldChosenSearchValue = null
        if (this.searchMarker) {
          this.searchMarker.setMap(null)
        }
        console.error(exception)
      }
    },

    updateMapType (type) {
      try {
        const { url, key } = type || {}
        // Update map type per user
        this.setMapType(type)
        let googleType = ''

        // If map is not available, do nothing
        if (!this.map && !this.googleMaps) {
          return
        }

        // Map google type keys
        if (key === 'google') {
          googleType = 'roadmap'
        }
        else if (key === 'google-satellite') {
          googleType = 'satellite'
        }
        else if (key === 'google-roadmap-traffic') {
          googleType = 'traffic'
        }

        // Remove current tile layer before applying new one
        if (this.currentTileLayer) {
          this.map?.overlayMapTypes?.pop()
          this.currentTileLayer = null
        }

        // Applying google map type
        if (['roadmap', 'satellite', 'traffic'].includes(googleType)) {
          // Set built-in Google Map types
          this.map?.setMapTypeId(
            googleType === 'traffic' ? this.googleMaps?.MapTypeId?.ROADMAP : this.googleMaps?.MapTypeId[googleType?.toUpperCase()]
          )

          // If it's traffic, enable traffic layer
          if (googleType === 'traffic') {
            if (!this.trafficLayer) {
              this.trafficLayer = new this.googleMaps.TrafficLayer()
            }
            this.trafficLayer?.setMap(this.map)
          }
          else if (this.trafficLayer) {
            this.trafficLayer?.setMap(null)
          }

          return
        }

        // If a map type is not one of the googles map types, url should be presented
        if (!url) {
          return
        }

        const tileLayer = new this.googleMaps.ImageMapType({
          getTileUrl: (coord, zoom) => url.replace('{x}', coord.x).replace('{y}', coord.y).replace('{z}', zoom),
          tileSize: new this.googleMaps.Size(256, 256),
          maxZoom: 20
        })

        this.map?.overlayMapTypes?.push(tileLayer)
        this.currentTileLayer = tileLayer
      }
      catch (exception) {
        console.warn(exception)
      }
    },

    expandSearch () {
      this.searchExpanded = true
      this.chosenSearchValue = this.oldChosenSearchValue

      if (this.searchMarker && !this.chosenSearchValue) {
        this.searchMarker.setMap(null)
      }
      this.$refs['search-autocomplete'].$el.style.width = '250px'
    },

    collapseSearch () {
      this.searchExpanded = false
      this.chosenSearchValue = null
      this.$refs['search-autocomplete'].$el.style.width = '40px'
    },

    onChooseAddress (event) {
      this.oldChosenSearchValue = event

      // Show the search address on the map
      if (this.map && this.googleMaps && this.AdvancedMarker && event && event.x && event.y) {
        if (this.searchMarker) {
          this.searchMarker.setMap(null)
        }

        this.searchMarker = new this.AdvancedMarker({
          map: this.map,
          position: { lat: event.y, lng: event.x }
        })

        const bounds = new this.googleMaps.LatLngBounds()
        bounds.extend(this.searchMarker.position)

        this.map.fitBounds(bounds)

        this.map.setZoom(DEFAULT_MAP_SEARCH_ZOOM_LEVEL)
      }
      else if (this.searchMarker) {
        this.searchMarker.setMap(null)
      }
    },

    onSearchClearClick (event) {
      this.oldChosenSearchValue = null
      this.searchValueItems = []
      if (this.searchMarker) {
        this.searchMarker.setMap(null)
      }
    },

    // Set custom map styles via config
    getMapStyle () {
      const { style } = this.config || {}
      let mapStyle = {}

      if (style && isObject(style)) {
        mapStyle = {
          ...mapStyle,
          ...style
        }
      }

      return mapStyle
    },

    // Set custom map classes via config
    getMapClasses () {
      const { class: mapClasses } = this.config || {}
      let classes = ''

      if (mapClasses && typeof mapClasses === 'string') {
        classes += ` ${mapClasses}`
      }

      return classes
    },

    onMapTypeChooseOpen (status) {
      this.$nextTick(() => {
        if (this.showMapTypeChooserControl) {
          if (status) {
            this.$refs.mapTypeChooser.style.width = '100%'
          }
          else {
            this.$refs.mapTypeChooser.style.width = '75px'
          }
        }
      })
    }
  }
}
</script>

<style scoped>
.google-map {
  width: 100%;
  height: 100%;
}

.google-map-transition {
  opacity: 0;
  transition: opacity 0.3s ease-in-out;
}

.google-map-transition.loaded {
  opacity: 1;
}

.map-wrapper {
  position: relative;
  height: 100%;
  width: 100%;
}

.map-type-chooser {
  width: 100%;
  margin-left: 10px;
}

.search-container {
  position: absolute;
  top: 10px;
  left: 10px;
  transition: all 0.3s ease-in-out;
  z-index: 10;
}

.search-input {
  width: 40px;
  height: 40px;
  padding: 5px;
  border-radius: 8px;
  transition: width 0.3s ease-in-out;
  background-color: white;
  margin: 0 10px 10px 10px;
}

.search-input:focus {
  width: 250px;
  padding-left: 10px;
}

::v-deep {
  .gm-bundled-control .gmnoprint div, .gm-bundled-control button {
    border-radius: 8px !important;
  }

  .v-input .v-input__slot::before {
    border: unset !important;
  }

  div.v-menu__content {
    max-width: 250px !important;
  }

  .v-input__append-inner {
    padding-left: unset !important;
  }

  .gm-bundled-control .gmnoprint div .gm-control-active img {
    width: 20px !important;
    height: 20px !important;
  }
}
</style>
